/// <reference types="@types/google.maps" />

import React, { useState, useEffect, useRef } from 'react';
import geohashLib from 'ngeohash';
import { Event } from '../../../types/event.types';
// Removed unused Autocomplete import
import { StepIndicator, Icon, Button } from '../../../pages/DesignSystem';
import { PlaceAutocompleteResult } from '../../shared/PlaceAutocomplete';
import BasicInfoStep from './steps/BasicInfoStep';
import DetailsStep from './steps/DetailsStep';
import { useOrganizer } from '../../../contexts/OrganizerContext';
import PreviewStep from './steps/PreviewStep';
import { cloudinaryService } from '../../../services/cloudinary.service';
import { FormData, initialFormData } from './types';
import { handleRecurringEvent, updateRecurringEvent, UpdateRecurringResult } from './handleRecurringEvent';
import { cleanEventData, extractTimeFromEndTime } from '../../../utils/eventUtils';
import { getTimezoneFromCoords, localToUTC, localEndTimeToUTC, formatEventTime, formatEventDate } from '../../../utils/timezoneUtils';
import ConfirmationModal from '../EventList/ConfirmationModal';
import { RecurrenceChangePreview } from '../../../types/eventSeries.types';
import './EventForm.css';
import { toast } from 'react-hot-toast';
import { supabaseEventSeriesService } from '../../../supabase/eventSeries.service';
import { supabaseEventService } from '../../../supabase/event.service';
import { locationCache } from '../../../services/LocationCacheService';
import { draftSuccessMessages, publishSuccessMessages, getRandomToastMessage } from '../../../components/messages/toastMessages';
import { useGoogleMaps } from '../../../contexts/GoogleMapsContext';
import { SecurityUtils } from '../../../utils/security';
import { validateForUser } from '../../../utils/validationRules';
import { useAnalytics } from '../../../contexts/AnalyticsContext';
import stripeService from '../../../supabase/stripe.service';
import PublishLimitModal from '../EventList/PublishLimitModal';

// Precision geohash : 6 = ~1.2km x 0.6km (optimal pour quartier)
const GEOHASH_PRECISION = 6;

interface EventFormProps {
  event?: Event & { seriesId?: string };  // Ajout de seriesId pour les événements récurrents
  organizerId: string;
  onSubmit: (eventData: Partial<Omit<Event, 'id'>> & { id?: string }) => void;
  onClose: () => void;
  isEditingOccurrence?: boolean; // Nouveau paramètre pour indiquer qu'on édite une occurrence
  mode?: 'modal' | 'sidebar'; // Mode d'affichage (modal = overlay, sidebar = intégré)
  onFormDataChange?: (data: FormData) => void; // Callback for live preview
  onStepChange?: (step: number) => void; // Callback when step changes
  hideStepperAndPreview?: boolean; // Hide stepper and skip step 3 (desktop modal with live preview)
}

const FORM_STEPS = [
  {
    title: "Basic Info",
    description: "Title, date and location"
  },
  {
    title: "Details",
    description: "Description, price and tags"
  },
  {
    title: "Review",
    description: "Preview and publish"
  }
];

const EventForm: React.FC<EventFormProps> = ({ event, organizerId, onSubmit, onClose, isEditingOccurrence = false, mode = 'modal', onFormDataChange, onStepChange, hideStepperAndPreview = false }) => {
  const { organizerProfile } = useOrganizer();
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const [showLocationNameInput, setShowLocationNameInput] = useState(false);
  const [isLoadingTimezone] = useState(false); // 🌍 État de chargement timezone
  const formRef = useRef<HTMLDivElement>(null);

  // 🐢 FIX (robustesse réseau faible - item 5d): mémorise l'id du brouillon créé lors
  // d'une tentative de publication d'un NOUVEL événement. Si la publication échoue après
  // la création du brouillon (ex: appel RPC publish_event qui timeout sur réseau lent),
  // un nouveau clic sur "Publier" doit réutiliser ce brouillon au lieu d'en recréer un
  // 2e (ce qui laissait des brouillons fantômes en double). Réinitialisé si le formulaire
  // change d'événement cible (édition d'un événement existant vs création).
  const createdDraftEventIdRef = useRef<string | null>(null);
  useEffect(() => {
    createdDraftEventIdRef.current = null;
  }, [event?.id]);

  // Scroll to top of nearest scrollable parent on step change
  useEffect(() => {
    const el = formRef.current;
    if (!el) return;
    // Find the scrollable parent (event-form-overlay or desktop-event-form__form-section)
    let scrollParent: HTMLElement | null = el.parentElement;
    while (scrollParent) {
      const { overflowY } = getComputedStyle(scrollParent);
      if (overflowY === 'auto' || overflowY === 'scroll') break;
      scrollParent = scrollParent.parentElement;
    }
    scrollParent?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  // ===== CONFIRMATION MODAL STATE =====
  // Type for pending recurrence change confirmation
  interface RecurrenceConfirmState {
    isOpen: boolean;
    preview: RecurrenceChangePreview | null;
    pendingAction: (() => Promise<void>) | null;
    variant: 'warning' | 'danger' | 'info';
  }
  const [recurrenceConfirm, setRecurrenceConfirm] = useState<RecurrenceConfirmState>({
    isOpen: false,
    preview: null,
    pendingAction: null,
    variant: 'warning'
  });

  // ===== ANALYTICS PHASE 2 - TRACKING DU FORMULAIRE =====
  const analytics = useAnalytics();
  const [formStartTime] = useState(Date.now());
  const [stepStartTime, setStepStartTime] = useState(Date.now());
  const [fieldModificationCounts, setFieldModificationCounts] = useState<Record<string, number>>({});
  const [hasTriggeredCreationStart, setHasTriggeredCreationStart] = useState(false);

  // ===== PUBLISH LIMIT MODAL STATE =====
  const [showPublishLimit, setShowPublishLimit] = useState(false);
  const [publishLimitEventId, setPublishLimitEventId] = useState<string | null>(null);
  const [publishLimitQuota, setPublishLimitQuota] = useState({ used: 0, limit: 5 });

  // Fonction utilitaire pour calculer la complétude
  const calculateCompleteness = (data: FormData, step: 'basic_info' | 'details' | 'preview'): number => {

    const allFields = ['title', 'location', 'locationName', 'date', 'time', 'description', 'tags', 'prices'];
    const filledFields = allFields.filter(field => {
      const value = data[field as keyof FormData];
      if (Array.isArray(value)) return value.length > 0;
      return value && value.toString().trim() !== '';
    });
    
    const completion = Math.round((filledFields.length / allFields.length) * 100);
    
    return completion;
  };

  // Fonction utilitaire pour obtenir les champs remplis
  const getFilledFields = (data: FormData): string[] => {
    const allFields = ['title', 'location', 'locationName', 'date', 'time', 'description', 'tags', 'prices', 'image'];
    return allFields.filter(field => {
      const value = data[field as keyof FormData];
      if (Array.isArray(value)) return value.length > 0;
      return value && value.toString().trim() !== '';
    });
  };

  // Fonction utilitaire pour obtenir les erreurs de validation
  const getValidationErrors = (errors: Partial<Record<keyof FormData, string>>): string[] => {
    return Object.keys(errors).filter(key => errors[key as keyof FormData]);
  };

  // 🌍 Formater une date UTC pour les inputs (YYYY-MM-DD et HH:MM)
  // Utilise le timezone de l'événement pour afficher l'heure locale correcte
  const formatDateForInput = (datetime: Date | undefined | null, timezone?: string): { date: string, time: string } => {
    if (!datetime) return { date: '', time: '' };
    try {
      const d = new Date(datetime);
      if (isNaN(d.getTime())) return { date: '', time: '' };

      // ✅ CORRECTION : Ne pas auto-remplir pour les drafts
      // Si c'est un draft sans date explicite, laisser vide
      if (!datetime || datetime.toString() === 'Invalid Date') {
        return { date: '', time: '' };
      }

      // 🌍 Si on a un timezone, utiliser les utilitaires timezone pour obtenir l'heure locale de l'événement
      if (timezone) {
        const date = formatEventDate(d, timezone, 'yyyy-MM-dd');
        const time = formatEventTime(d, timezone, 'HH:mm');
        console.log(`🌍 [formatDateForInput] Conversion UTC → ${timezone}: ${d.toISOString()} → ${date} ${time}`);
        return { date, time };
      }

      // Fallback: utiliser l'heure locale du navigateur (pour les nouveaux événements sans timezone)
      // Format YYYY-MM-DD pour input date
      const date = d.toISOString().split('T')[0];

      // Format HH:MM pour input time
      const hours = String(d.getHours()).padStart(2, '0');
      const minutes = String(d.getMinutes()).padStart(2, '0');
      const time = `${hours}:${minutes}`;

      return { date, time };
    } catch {
      return { date: '', time: '' };
    }
  };

  const [formData, setFormData] = useState<FormData>({
    ...initialFormData,
    organizerId: organizerId,
    organizerContact: event?.organizerContact || organizerProfile?.organizerContact || '',
    location: event?.location || organizerProfile?.location || '',
    locationName: event?.locationName || organizerProfile?.locationName || '',
    latitude: event?.latitude || organizerProfile?.latitude || 0,
    longitude: event?.longitude || organizerProfile?.longitude || 0,
    ...(event && {
      ...event,
      timezone: event.timezone || 'Asia/Colombo', // 🌍 Charger le timezone de l'événement
      ...(event.datetime && {
        // 🌍 Convertir l'heure UTC vers l'heure locale de l'événement
        ...formatDateForInput(event.datetime, event.timezone)
      }),
      ...(event.endTime && {
        endTime: extractTimeFromEndTime(event.endTime, event.timezone)
      })
    })
  });

  // ===== EFFET DE TRACKING INITIAL =====
  useEffect(() => {
    // Charger correctement les données d'un événement existant
    if (event) {
      console.log('Chargement d\'un événement existant:', event);
      
      // 🌍 Récupérer le timezone de l'événement existant
      const eventTimezone = event.timezone || 'Asia/Colombo';
      console.log(`🌍 [EventForm] Timezone de l'événement existant: ${eventTimezone}`);

      let initialData: FormData = {
        ...initialFormData,
        organizerId: organizerId,
        organizerContact: event.organizerContact || organizerProfile?.organizerContact || '',
        location: event.location || '',
        locationName: event.locationName || '',
        latitude: event.latitude || 0,
        longitude: event.longitude || 0,
        // ⚠️ geohash = signal "coordonnées confirmées" utilisé par validateForUser en mode publish
        // (voir PRD fiabilité découverte touriste, item 2). Sans lui, un event existant déjà publié
        // et géolocalisé se ferait bloquer à la republication alors que sa localisation n'a pas changé.
        geohash: event.geohash || undefined,
        timezone: eventTimezone, // 🌍 Charger le timezone de l'événement
        title: event.title || '',
        description: event.description || '',
        tags: event.tags || [],
        prices: event.prices || [],
        ticketingUrl: event.ticketingUrl || '',
        image: event.image || '',
        status: event.status || 'draft',
        id: event.id,
        endTime: event.endTime ? extractTimeFromEndTime(event.endTime, event.timezone) : ''
      };
      
      // Traitement spécifique selon le type d'événement (récurrent ou non)
      if (event.seriesId) {
        console.log('Événement récurrent détecté avec seriesId:', event.seriesId);
        console.log('Mode édition occurrence:', isEditingOccurrence);
        
        if (isEditingOccurrence) {
          // 🎯 Mode édition d'occurrence individuelle : traiter comme un événement simple
          console.log('Mode édition d\'occurrence - affichage en événement simple');
          // 🌍 Passer le timezone pour convertir l'heure UTC vers l'heure locale de l'événement
          const { date, time } = formatDateForInput(event.datetime, eventTimezone);
          initialData = {
            ...initialData,
            isRecurring: false, // ✨ Important : masquer l'interface de récurrence
            isEditingOccurrence: true, // ✨ Marquer qu'on édite une occurrence
            seriesId: event.seriesId, // ✨ Conserver la référence à la série
            date,
            time,
            endTime: event.endTime ? extractTimeFromEndTime(event.endTime, event.timezone) : ''
          };

          setFormData(initialData);
        } else {
          // Mode édition de toute la série : charger les données de récurrence
          console.log('Mode édition de série complète - chargement des données de récurrence');

          // 🔧 FIX: Utiliser un flag isMounted pour éviter les mises à jour sur composant démonté
          let isMounted = true;

          const loadSeriesData = async () => {
            try {
              console.log('⏳ [EventForm] Début chargement série:', event.seriesId);

              // 1. D'abord essayer le cache IndexedDB (instantané, évite appel Supabase)
              let seriesData = await locationCache.getSeries(event.seriesId as string);

              if (seriesData) {
                console.log('💾 [EventForm] Cache HIT - série chargée depuis IndexedDB');
              } else {
                // 2. Si pas en cache, fallback sur Supabase (rare)
                console.log('📭 [EventForm] Cache miss, appel Supabase...');
                seriesData = await supabaseEventSeriesService.getEventSeriesById(event.seriesId as string);
              }

              console.log('📦 [EventForm] Données de série reçues:', seriesData ? 'OK' : 'NULL');

              // 🔧 FIX: Vérifier si le composant est toujours monté avant de mettre à jour le state
              if (!isMounted) {
                console.log('⚠️ [EventForm] Composant démonté, abandon de la mise à jour');
                return;
              }

              if (seriesData) {
                console.log('✅ [EventForm] Application des données de série au formulaire');
                // 🌍 Utiliser le timezone de la série
                const seriesTimezone = seriesData.timezone || eventTimezone;
                console.log(`🌍 [EventForm] Timezone de la série: ${seriesTimezone}`);

                const seriesFormData: FormData = {
                  ...initialData,
                  isRecurring: true,
                  timezone: seriesTimezone, // 🌍 Charger le timezone de la série
                  startDate: seriesData.startDate || '',
                  startTime: seriesData.startTime || '',
                  endTime: seriesData.endTime || '',
                  frequency: seriesData.frequency || 'weekly',
                  weekDays: seriesData.weekDays || [],
                  daySchedules: seriesData.daySchedules ?? null,
                  endType: seriesData.endType || 'date',
                  endDate: seriesData.endDate || '',
                  occurrences: seriesData.occurrences
                };

                setFormData(seriesFormData);
                console.log('✅ [EventForm] FormData mis à jour avec données de série');
              } else {
                console.warn('⚠️ [EventForm] Série non trouvée:', event.seriesId);
                toast.error('Impossible de trouver les données de la série');
              }
            } catch (error) {
              console.error('❌ [EventForm] Erreur lors du chargement des données de série:', error);
              if (isMounted) {
                toast.error('Impossible de charger toutes les informations de l\'événement récurrent');
              }
            }
          };

          loadSeriesData();

          // 🔧 FIX: Cleanup pour marquer le composant comme démonté
          // Note: Cette approche n'est pas idéale mais fonctionne pour ce cas
          // Le cleanup de l'useEffect parent va nettoyer
        }
      } else if (event.datetime) {
        // Événement ponctuel
        // 🌍 Passer le timezone pour convertir l'heure UTC vers l'heure locale de l'événement
        const { date, time } = formatDateForInput(event.datetime, eventTimezone);
        initialData = {
          ...initialData,
          isRecurring: false,
          date,
          time,
          endTime: event.endTime ? extractTimeFromEndTime(event.endTime, event.timezone) : ''
        };

        setFormData(initialData);
      }
    }

    // 🔧 FIX: Cleanup function - will be called when effect re-runs or component unmounts
    // Note: The isMounted flag inside loadSeriesData handles this
  }, [event, organizerId, organizerProfile, isEditingOccurrence]);

  useEffect(() => {
    // Tracker le début de création seulement une fois et si ce n'est pas un événement existant
    if (!hasTriggeredCreationStart && !event) {
      console.log('🚀 [Analytics] Début de création d\'événement tracké');
      
      const typeEvenement = formData.isRecurring ? 'recurrent' : 'unique';
      const estPremierEvenement = true; // TODO: calculer basé sur l'historique de l'organisateur
      const aDraftsExistants = false; // TODO: vérifier s'il a des drafts existants
      
      analytics.trackCreationEventStart(typeEvenement, estPremierEvenement, aDraftsExistants);
      analytics.trackOrganizerNavigation('creation', 0, ['ouverture_formulaire']);
      
      setHasTriggeredCreationStart(true);
      
      console.log('📊 [Analytics] Événement creation_evenement_debut envoyé:', {
        type_evenement: typeEvenement,
        est_premier_evenement: estPremierEvenement,
        a_drafts_existants: aDraftsExistants
      });
    }
  }, [hasTriggeredCreationStart, event, formData.isRecurring]);

  // ===== CALLBACKS FOR LIVE PREVIEW =====
  // Notify parent of form data changes for live preview
  useEffect(() => {
    if (onFormDataChange) {
      onFormDataChange(formData);
    }
  }, [formData, onFormDataChange]);

  // Notify parent of step changes
  useEffect(() => {
    if (onStepChange) {
      onStepChange(currentStep);
    }
  }, [currentStep, onStepChange]);

  // 🌍 Détection initiale du timezone si coordonnées pré-remplies (location organisateur)
  useEffect(() => {
    const detectInitialTimezone = async () => {
      // Ne détecter que si:
      // 1. On a des coordonnées
      // 2. Le timezone est le fallback par défaut ou pas défini
      // 3. Ce n'est pas un événement existant (qui a déjà un timezone)
      const hasCoords = formData.latitude && formData.longitude;
      const needsTimezoneDetection = !formData.timezone || formData.timezone === 'Asia/Colombo';
      const isNewEvent = !event;

      // Double vérification pour TypeScript (hasCoords garantit déjà que les coordonnées existent)
      if (hasCoords && needsTimezoneDetection && isNewEvent && typeof formData.latitude === 'number' && typeof formData.longitude === 'number') {
        console.log('🌍 [Timezone] Détection initiale pour coordonnées pré-remplies...');
        // tz-lookup est synchrone, pas besoin de loading state
        const detectedTimezone = getTimezoneFromCoords(formData.latitude, formData.longitude);
        console.log(`✅ [Timezone] Timezone initial détecté: ${detectedTimezone}`);
        setFormData(prev => ({ ...prev, timezone: detectedTimezone }));
      }
    };

    detectInitialTimezone();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Exécuter une seule fois au montage

  const validateForPublication = (): { valid: boolean, errors: Record<string, string> } => {
    return validateForUser(formData, 'event', 'publish');
  };

  const handleSubmit = async (e: React.MouseEvent, isDraft: boolean) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Validation selon le mode (brouillon ou publication)
      if (isDraft) {
        // Validation minimale pour brouillon
        if (!formData.title) {
          setErrors({ title: 'Title is required even for a draft' });
          setIsSubmitting(false);
          return;
        }
      } else {
        // Validation complète pour publication
        const validation = validateForPublication();
        if (!validation.valid) {
          setErrors(validation.errors);
          // Montrer un message d'erreur explicatif
          toast.error('Some information is missing to publish the event');
          // Faire défiler jusqu'à la première erreur
          const firstErrorField = document.querySelector('[aria-invalid="true"]');
          if (firstErrorField) {
            firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
          setIsSubmitting(false);
          return;
        }
      }
      
      let finalImageUrl = formData.image || '';
      if (finalImageUrl && (finalImageUrl.startsWith('blob:') || finalImageUrl.startsWith('data:'))) {
        console.warn('Safety net: blob/data URL detected at submission, converting to Cloudinary...');
        try {
          const response = await fetch(finalImageUrl);
          const blob = await response.blob();
          const file = new File([blob], 'image.jpg', { type: blob.type });
          const cloudinaryUrl = await cloudinaryService.uploadImage(file);
          if (cloudinaryUrl && !cloudinaryUrl.startsWith('blob:')) {
            finalImageUrl = cloudinaryUrl;
          } else {
            console.error('Safety net: Cloudinary returned invalid URL, clearing image');
            finalImageUrl = '';
          }
        } catch (error) {
          console.error('Safety net: failed to convert blob to Cloudinary:', error);
          finalImageUrl = '';
        }
      }
      // Final guard: NEVER save a blob: URL to the database
      if (finalImageUrl.startsWith('blob:') || finalImageUrl.startsWith('data:')) {
        console.error('CRITICAL: blob/data URL about to be saved, blocking it');
        finalImageUrl = '';
      }

      // Avant la soumission, sanitiser les champs texte :
      const sanitizedFormData = {
        ...formData,
        title: SecurityUtils.sanitizeInputForSubmission(formData.title || ''),
        description: SecurityUtils.sanitizeInputForSubmission(formData.description || ''),
        location: SecurityUtils.sanitizeInputForSubmission(formData.location || ''),
        locationName: SecurityUtils.sanitizeInputForSubmission(formData.locationName || ''),
        organizerContact: SecurityUtils.sanitizeInputForSubmission(formData.organizerContact || ''),
        ticketingUrl: formData.ticketingUrl ? SecurityUtils.sanitizeInputForSubmission(formData.ticketingUrl) : '',
        // Pour les prix, description de chaque prix :
        prices: (formData.prices || []).map(price => ({
          ...price,
          description: SecurityUtils.sanitizeInputForSubmission(price.description || '')
        }))
      };

      const updatedFormData = {
        ...sanitizedFormData,
        image: finalImageUrl,
        status: isDraft ? 'draft' as const : 'active' as const
      };

      console.log('FormData mis à jour:', updatedFormData);

      if (formData.isEditingOccurrence) {
        // 🎯 Modification d'une occurrence individuelle
        console.log('Modification d\'occurrence individuelle détectée');
        
        if (!formData.seriesId || !event?.id) {
          throw new Error('SeriesId ou eventId manquant pour la modification d\'occurrence');
        }

        // Construire les données spécifiques à l'occurrence
        // 🌍 Utiliser localToUTC pour convertir l'heure locale de l'événement en UTC
        let eventDateTime = undefined;
        if (formData.date && formData.time) {
          const utcISO = localToUTC(formData.date, formData.time, formData.timezone);
          eventDateTime = new Date(utcISO);
        }

        // Nettoyer les données pour l'occurrence
        const cleanedData = cleanEventData(updatedFormData);
        // 🌍 Convertir endTime en UTC avec le timezone de l'événement
        const startUTC = formData.date && formData.time
          ? localToUTC(formData.date, formData.time, formData.timezone)
          : undefined;
        const endTimeISO = startUTC
          ? localEndTimeToUTC(formData.date!, formData.time!, formData.endTime, formData.timezone, startUTC)
          : formData.endTime;

        const occurrenceData = {
          ...cleanedData,
          datetime: eventDateTime,
          endTime: endTimeISO,
          seriesId: formData.seriesId, // ✨ Conserver la référence à la série
          isException: true, // ✨ Marquer comme exception
          updatedAt: new Date()
        };

        console.log('Données d\'occurrence à sauvegarder:', occurrenceData);
        
        // Utiliser updateSingleEvent pour modifier juste cette occurrence
        await supabaseEventSeriesService.updateSingleEvent(event.id, occurrenceData);
        
        showSuccessToast(isDraft);
      } else if (formData.isRecurring) {
        // Gestion des événements récurrents (mode série complète)
        if (event?.seriesId) {
          // Modification d'un événement récurrent existant
          // Pour l'instant on met à jour toute la série par défaut
          const updateAll = true;

          // Appel initial sans skipConfirmation
          const result: UpdateRecurringResult = await updateRecurringEvent(event.seriesId, updatedFormData, updateAll);

          // Si une confirmation est requise (des occurrences vont être supprimées)
          if (result.requiresConfirmation && result.preview) {
            // Ouvrir la modale de confirmation au lieu de window.confirm()
            setRecurrenceConfirm({
              isOpen: true,
              preview: result.preview,
              pendingAction: async () => {
                await updateRecurringEvent(event.seriesId!, updatedFormData, updateAll, { skipConfirmation: true });
                showSuccessToast(isDraft);
                analytics.trackEventPublished(
                  event?.id || 'new-draft',
                  Math.round((Date.now() - formStartTime) / 1000),
                  1,
                  event?.status === 'draft',
                  calculateCompleteness(formData, 'preview'),
                  'recurrent'
                );
                onClose();
              },
              variant: result.preview.toDelete.length > 0 ? 'danger' : 'warning'
            });
            setIsSubmitting(false);
            return; // Arrêter ici, la modale va gérer la suite
          }
        } else {
          // Création d'une nouvelle série d'événements
          await handleRecurringEvent(updatedFormData);
        }
      } else {
        // Événement unique
        // 🌍 Utiliser localToUTC pour convertir l'heure locale de l'événement en UTC
        let eventDateTime = undefined;
        if (formData.date && formData.time) {
          const utcISO = localToUTC(formData.date, formData.time, formData.timezone);
          eventDateTime = new Date(utcISO);
        }

        try {
          // Nettoyer les données avant la soumission
          const cleanedData = cleanEventData(updatedFormData);
          console.log('Données nettoyées:', cleanedData);

          // 🌍 Convertir endTime en UTC avec le timezone de l'événement
          const startUTC = formData.date && formData.time
            ? localToUTC(formData.date, formData.time, formData.timezone)
            : undefined;
          const endTimeISO = startUTC
            ? localEndTimeToUTC(formData.date!, formData.time!, formData.endTime, formData.timezone, startUTC)
            : formData.endTime;

          if (endTimeISO !== formData.endTime) {
            console.log(`🌍 endTime converti en UTC: "${formData.endTime}" → "${endTimeISO}"`);
          }

          const submissionData = {
            ...cleanedData,
            datetime: eventDateTime,
            endTime: endTimeISO,
            createdAt: new Date(),
            updatedAt: new Date(),
            ...(event?.id && { id: event.id })
          };

          console.log('Données finales à soumettre:', submissionData);
          await onSubmit(submissionData);

          showSuccessToast(isDraft);
        } catch (error) {
          console.error('Erreur lors du nettoyage ou de la soumission:', error);
          throw error;
        }
      }

      onClose();
    } catch (error) {
      console.error('Erreur lors de la soumission:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNext = () => {
    console.log('🔍 [Analytics] Tentative de passage à l\'étape suivante depuis:', currentStep);
    
    // Calculs pour l'analytics
    const tempsPasseEtape = Math.round((Date.now() - stepStartTime) / 1000);
    const champsRemplis = getFilledFields(formData);
    const erreursRencontrees = getValidationErrors(errors);
    const completudePourcentage = calculateCompleteness(formData, 
      currentStep === 0 ? 'basic_info' : currentStep === 1 ? 'details' : 'preview'
    );
    
    // Validation pour étape 0 (Basic Info)
    if (currentStep === 0) {
      if (!formData.title || formData.title.trim() === '') {
        console.log('❌ [Analytics] Erreur de validation - titre manquant');
        setErrors({ title: 'Title is required to continue' });
        
        // Faire défiler jusqu'au champ titre
        const titleField = document.querySelector('[name="title"]');
        if (titleField) {
          titleField.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        
        toast.error('Please add a title to continue', {
          icon: '✏️',
          style: {
            borderRadius: '10px',
            background: '#FFF8E1',
            color: '#333',
          },
        });
        return;
      }
      
      // Tracker la complétion de l'étape avec analytics
      analytics.trackFormStepCompleted(
        'basic_info',
        tempsPasseEtape,
        champsRemplis,
        erreursRencontrees,
        completudePourcentage,
        formData.isRecurring ? 'recurrent' : 'unique'
      );
      
      console.log('📊 [Analytics] Étape basic_info complétée:', {
        temps_passe: tempsPasseEtape,
        champs_remplis: champsRemplis,
        erreurs: erreursRencontrees,
        completude: completudePourcentage
      });
    }
    
    // Si la validation passe, passer à l'étape suivante
    console.log(`✅ [Analytics] Passage de l'étape ${currentStep} à l'étape ${currentStep + 1}`);
    setCurrentStep(prevStep => prevStep + 1);
    setStepStartTime(Date.now()); // Reset timer pour la nouvelle étape
  };

  const handlePrevious = () => {
    console.log('🔙 [Analytics] Retour à l\'étape précédente depuis:', currentStep);
    
    // Tracker l'étape précédente comme "incomplète" ou "retour en arrière"
    const tempsPasseEtape = Math.round((Date.now() - stepStartTime) / 1000);
    const etapeActuelle = currentStep === 1 ? 'details' : 'preview';
    
    console.log('📊 [Analytics] Retour en arrière tracké:', {
      etape_quittee: etapeActuelle,
      temps_passe: tempsPasseEtape
    });
    
    setCurrentStep(prev => Math.max(prev - 1, 0));
    setStepStartTime(Date.now());
  };

  const handleSaveAsDraft = (e: React.MouseEvent) => {
    console.log('💾 [Analytics] Tentative de sauvegarde en draft');
    
    const tempsAvantSauvegarde = Math.round((Date.now() - formStartTime) / 1000);
    const champsRemplis = getFilledFields(formData);
    const completudePourcentage = calculateCompleteness(formData, 
      currentStep === 0 ? 'basic_info' : currentStep === 1 ? 'details' : 'preview'
    );
    const etapeSauvegarde = currentStep === 0 ? 'basic_info' : currentStep === 1 ? 'details' : 'preview';
    
    // Générer un ID de draft temporaire pour le tracking
    const draftId = event?.id || `draft_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const estPremierDraft = !event; // C'est un premier draft si ce n'est pas un événement existant
    
    analytics.trackDraftSaved(
      draftId,
      etapeSauvegarde,
      completudePourcentage,
      champsRemplis,
      estPremierDraft,
      tempsAvantSauvegarde
    );
    
    console.log('📊 [Analytics] Draft sauvegardé:', {
      id_draft: draftId,
      etape: etapeSauvegarde,
      completude: completudePourcentage,
      temps_avant_sauvegarde: tempsAvantSauvegarde,
      champs_remplis: champsRemplis
    });
    
    handleSubmit(e, true);
  };

  const handlePublish = async () => {
    console.log("�� handlePublish commencé, validation...");
    setIsSubmitting(true);
    
    // Vérifiez si tous les champs requis sont remplis
    const validation = validateForPublication();
    
    console.log("🔍 Erreurs de validation:", validation.errors);
    
    if (!validation.valid) {
      console.error("⚠️ Validation échouée:", validation.errors);
      setErrors(validation.errors);
      
      // Faire défiler jusqu'au premier champ en erreur
      const firstErrorField = document.querySelector('[aria-invalid="true"]');
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      
      // Créer un message détaillé avec la liste des informations manquantes
      const missingFields = Object.entries(validation.errors)
        .map(([field]) => {
          // Convertir le nom du champ en texte plus lisible
          const fieldNameMap: Record<string, string> = {
            title: "Title",
            description: "Description",
            location: "Location",
            locationName: "Venue name",
            date: "Date",
            time: "Start time",
            endTime: "End time",
            startDate: "Series start date",
            startTime: "Series start time",
            weekDays: "Days of the week",
            image: "Event image"
          };
          
          return `${fieldNameMap[field] || field}`;
        });
      
      // Afficher le toast avec la liste des informations manquantes
      toast.error(
        <div style={{ padding: '4px 0' }}>
          <div style={{ 
            marginBottom: '10px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span style={{ fontSize: '18px' }}>✨</span>
            <h4 style={{ 
              margin: 0, 
              fontWeight: '600',
              fontSize: '15px'
            }}>
              Almost there! Your event needs a few more details:
            </h4>
          </div>
          <ul style={{ 
            margin: '0 0 12px 0',
            paddingLeft: '24px',
            listStyleType: 'circle'
          }}>
            {missingFields.map((field, index) => (
              <li key={index} style={{ 
                padding: '3px 0',
                fontSize: '14px'
              }}>
                {field}
              </li>
            ))}
          </ul>
          <div style={{
            fontSize: '13px',
            fontStyle: 'italic',
            background: 'rgba(255,255,255,0.5)',
            padding: '8px 10px',
            borderRadius: '8px',
            marginTop: '4px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}>
            <span style={{ fontSize: '14px' }}>💡</span>
            <span>You can also save as draft and complete it later!</span>
          </div>
        </div>,
        {
          duration: 6000,
          style: {
            borderRadius: '12px',
            background: 'linear-gradient(to bottom, #FFF8E1, #FFFCF2)',
            color: '#664500',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            padding: '12px 16px',
            maxWidth: '350px'
          },
          icon: '📝',
        }
      );
      
      // Tracker la tentative de publication échouée
      const completudeActuelle = calculateCompleteness(formData, 'preview');
      analytics.trackFailedPublishAttempt(
        currentStep === 0 ? 'basic_info' : currentStep === 1 ? 'details' : 'preview',
        Object.keys(validation.errors), // Champs manquants
        completudeActuelle,
        Math.round((Date.now() - formStartTime) / 1000),
        1 // TODO: compteur de tentatives
      );

      console.log('📊 [Analytics] Tentative de publication échouée trackée:', {
        etape: currentStep === 0 ? 'basic_info' : currentStep === 1 ? 'details' : 'preview',
        champs_manquants: Object.keys(validation.errors),
        completude: completudeActuelle
      });

      setIsSubmitting(false);
      return;
    }
    
    try {
      console.log("🔍 Préparation des données pour soumission:", {
        isRecurring: formData.isRecurring,
        hasSeriesId: !!event?.seriesId
      });
      
      // Dédupliquer les prix avant soumission
      const uniquePrices: {amount: number | null; currency: string; description: string}[] = [];
      const seen = new Set();
      
      for (const price of formData.prices || []) {
        const priceKey = `${price.amount}-${price.currency}`;
        
        if (!seen.has(priceKey)) {
          seen.add(priceKey);
          uniquePrices.push({
            amount: price.amount === null ? null : (typeof price.amount === 'string' ? parseFloat(price.amount) : price.amount),
            currency: price.currency,
            description: SecurityUtils.sanitizeInputForSubmission(price.description || 'Standard price')
          });
        }
      }
      
      // Modifier directement formData
      formData.prices = uniquePrices;

      // 🖼️ Safety net: convertir les blob/data URLs en Cloudinary avant publication
      let finalImageUrl = formData.image || '';
      if (finalImageUrl && (finalImageUrl.startsWith('blob:') || finalImageUrl.startsWith('data:'))) {
        try {
          console.log('🖼️ [Publish] Blob URL détectée, upload vers Cloudinary...');
          const response = await fetch(finalImageUrl);
          const blob = await response.blob();
          const file = new File([blob], 'image.jpg', { type: blob.type });
          finalImageUrl = await cloudinaryService.uploadImage(file);
          console.log('🖼️ [Publish] Image uploadée:', finalImageUrl);
        } catch (error) {
          console.error('🖼️ [Publish] Erreur upload image:', error);
          finalImageUrl = '';
        }
      }

      // Forcer le statut à 'active' pour la publication
      const publishData = {
        ...formData,
        image: finalImageUrl,
        status: 'active' as 'active'
      };
      
      // Appel de la fonction de soumission
      if (formData.isEditingOccurrence) {
        // 🎯 Publication d'une occurrence individuelle modifiée
        console.log("🔍 Publication d'occurrence individuelle");
        
        if (!formData.seriesId || !event?.id) {
          throw new Error('SeriesId ou eventId manquant pour la publication d\'occurrence');
        }

        // Construire les données pour l'occurrence
        // 🌍 Utiliser localToUTC pour convertir l'heure locale de l'événement en UTC
        let eventDateTime = undefined;
        if (formData.date && formData.time) {
          const utcISO = localToUTC(formData.date, formData.time, formData.timezone);
          eventDateTime = new Date(utcISO);
        }

        const cleanedData = cleanEventData(publishData);
        // 🌍 Convertir endTime en UTC avec le timezone de l'événement
        const startUTC = formData.date && formData.time
          ? localToUTC(formData.date, formData.time, formData.timezone)
          : undefined;
        const endTimeISO = startUTC
          ? localEndTimeToUTC(formData.date!, formData.time!, formData.endTime, formData.timezone, startUTC)
          : formData.endTime;

        const occurrenceData = {
          ...cleanedData,
          datetime: eventDateTime,
          endTime: endTimeISO,
          seriesId: formData.seriesId,
          isException: true,
          status: 'active' as 'active',
          updatedAt: new Date()
        };

        await supabaseEventSeriesService.updateSingleEvent(event.id, occurrenceData);

        // ✨ Publier via RPC pour vérification quota (occurrence = part d'une série)
        console.log('📝 [Publish] Appel RPC publish_event pour occurrence:', event.id);
        const publishResult = await stripeService.publishEvent(event.id);

        if (!publishResult.success) {
          if (publishResult.error === 'limit_reached') {
            console.log('🚫 [Publish] Limite atteinte pour occurrence');
            setPublishLimitEventId(event.id);
            setPublishLimitQuota({
              used: publishResult.eventsUsed || 0,
              limit: publishResult.eventsLimit || 5
            });
            setShowPublishLimit(true);
            setIsSubmitting(false);
            return;
          }
          throw new Error(publishResult.error || 'Failed to publish occurrence');
        }

        // Tracking pour occurrence publiée
        const tempsTotalCreation = Math.round((Date.now() - formStartTime) / 1000);
        const completudeFinale = calculateCompleteness(formData, 'preview');

        analytics.trackEventPublished(
          event.id,
          tempsTotalCreation,
          1,
          event?.status === 'draft',
          completudeFinale,
          'unique' // ✨ Occurrence individuelle = événement unique
        );

        showSuccessToast(false);
      } else if (formData.isRecurring) {
        console.log("🔍 Traitement d'un événement récurrent");
        // Traiter comme événement récurrent
        if (event?.seriesId) {
          console.log("🔍 Mise à jour de série existante:", event.seriesId);

          // Appel initial sans skipConfirmation
          const result: UpdateRecurringResult = await updateRecurringEvent(event.seriesId, publishData);

          // Si une confirmation est requise (des occurrences vont être supprimées)
          if (result.requiresConfirmation && result.preview) {
            // Ouvrir la modale de confirmation au lieu de window.confirm()
            setRecurrenceConfirm({
              isOpen: true,
              preview: result.preview,
              pendingAction: async () => {
                await updateRecurringEvent(event.seriesId!, publishData, true, { skipConfirmation: true });

                // ✨ Publier via RPC pour vérification quota (série = 1 event)
                console.log('📝 [Publish] Appel RPC publish_series après confirmation:', event.seriesId);
                const publishResult = await stripeService.publishSeries(event.seriesId!);
                if (!publishResult.success && publishResult.error === 'limit_reached') {
                  setPublishLimitEventId(event.seriesId!);
                  setPublishLimitQuota({
                    used: publishResult.eventsUsed || 0,
                    limit: publishResult.eventsLimit || 5
                  });
                  setShowPublishLimit(true);
                  return;
                }

                showSuccessToast(false);
                // Tracking
                const tempsTotalCreation = Math.round((Date.now() - formStartTime) / 1000);
                const completudeFinale = calculateCompleteness(formData, 'preview');
                analytics.trackEventPublished(
                  event?.id || 'new-recurring',
                  tempsTotalCreation,
                  1,
                  event?.status === 'draft',
                  completudeFinale,
                  'recurrent'
                );
                onClose();
              },
              variant: result.preview.toDelete.length > 0 ? 'danger' : 'warning'
            });
            setIsSubmitting(false);
            return; // Arrêter ici, la modale va gérer la suite
          }

          // ✨ Publier via RPC pour vérification quota (série mise à jour sans confirmation)
          console.log('📝 [Publish] Appel RPC publish_series pour série existante:', event.seriesId);
          const seriesPublishResult = await stripeService.publishSeries(event.seriesId);
          if (!seriesPublishResult.success && seriesPublishResult.error === 'limit_reached') {
            setPublishLimitEventId(event.seriesId);
            setPublishLimitQuota({
              used: seriesPublishResult.eventsUsed || 0,
              limit: seriesPublishResult.eventsLimit || 5
            });
            setShowPublishLimit(true);
            setIsSubmitting(false);
            return;
          }
        } else {
          console.log("🔍 Création d'une nouvelle série");

          // ✨ Créer la série en draft d'abord, puis publier via RPC
          const draftSeriesData = { ...publishData, status: 'draft' as 'draft' };
          const seriesId = await handleRecurringEvent(draftSeriesData);
          console.log("🔍 handleRecurringEvent terminé, seriesId:", seriesId);

          // ✨ Publier via RPC avec vérification quota côté serveur
          console.log('📝 [Publish] Appel RPC publish_series pour nouvelle série:', seriesId);
          const newSeriesPublishResult = await stripeService.publishSeries(seriesId);

          if (!newSeriesPublishResult.success) {
            if (newSeriesPublishResult.error === 'limit_reached') {
              console.log('🚫 [Publish] Limite atteinte pour série');
              setPublishLimitEventId(seriesId);
              setPublishLimitQuota({
                used: newSeriesPublishResult.eventsUsed || 0,
                limit: newSeriesPublishResult.eventsLimit || 5
              });
              setShowPublishLimit(true);
              setIsSubmitting(false);
              return;
            }
            throw new Error(newSeriesPublishResult.error || 'Failed to publish series');
          }

          console.log('✅ [Publish] Série publiée via RPC avec succès');

          // ===== TRACKING PUBLICATION RÉCURRENTE =====
          const tempsTotalCreation = Math.round((Date.now() - formStartTime) / 1000);
          const nombreSessionsCreation = 1; // TODO: améliorer avec persistence entre sessions
          const etaitDraft = event?.status === 'draft';
          const completudeFinale = calculateCompleteness(formData, 'preview');
          const nombreOccurrences = formData.endType === 'occurrences' ? (formData.occurrences || 1) : undefined;
          
          // Tracker la configuration de récurrence
          analytics.trackRecurringEventConfigured(
            formData.frequency || 'weekly',
            formData.endType || 'date',
            30, // TODO: calculer la vraie durée de série
            formData.frequency === 'weekly' ? (formData.weekDays?.length || 0) : undefined,
            nombreOccurrences
          );
          
          console.log('📊 [Analytics] Événement récurrent configuré:', {
            frequence: formData.frequency,
            type_fin: formData.endType,
            nombre_occurrences: nombreOccurrences
          });
          
          // Tracker la publication finale
          analytics.trackEventPublished(
            seriesId,
            tempsTotalCreation,
            nombreSessionsCreation,
            etaitDraft,
            completudeFinale,
            'recurrent',
            nombreOccurrences
          );
          
          console.log('🎉 [Analytics] Série d\'événements publiée:', {
            id_serie: seriesId,
            temps_total_creation: tempsTotalCreation,
            etait_draft: etaitDraft,
            nombre_occurrences: nombreOccurrences
          });
        }
      } else {
        // Événement unique
        // 🌍 Utiliser localToUTC pour convertir l'heure locale de l'événement en UTC
        let eventDateTime = undefined;
        if (formData.date && formData.time) {
          const utcISO = localToUTC(formData.date, formData.time, formData.timezone);
          eventDateTime = new Date(utcISO);
        }

        try {
          // Nettoyer les données avant la soumission
          const cleanedData = cleanEventData(formData);
          // Sanitiser et trimmer les données textuelles
          const sanitizedData = {
            ...cleanedData,
            title: SecurityUtils.sanitizeInputForSubmission(cleanedData.title || ''),
            description: SecurityUtils.sanitizeInputForSubmission(cleanedData.description || ''),
            location: SecurityUtils.sanitizeInputForSubmission(cleanedData.location || ''),
            locationName: SecurityUtils.sanitizeInputForSubmission(cleanedData.locationName || ''),
            organizerContact: SecurityUtils.sanitizeInputForSubmission(cleanedData.organizerContact || ''),
            ticketingUrl: cleanedData.ticketingUrl ? SecurityUtils.sanitizeInputForSubmission(cleanedData.ticketingUrl) : '',
            prices: (cleanedData.prices || []).map(price => ({
              ...price,
              description: SecurityUtils.sanitizeInputForSubmission(price.description || '')
            }))
          };

          // 🌍 Convertir endTime en UTC avec le timezone de l'événement
          const startUTC = formData.date && formData.time
            ? localToUTC(formData.date, formData.time, formData.timezone)
            : undefined;
          const endTimeISO = startUTC
            ? localEndTimeToUTC(formData.date!, formData.time!, formData.endTime, formData.timezone, startUTC)
            : formData.endTime;

          // ✨ NOUVELLE LOGIQUE : Créer en draft d'abord, puis publier via RPC
          const draftData = {
            ...sanitizedData,
            datetime: eventDateTime,
            endTime: endTimeISO,
            createdAt: new Date(),
            updatedAt: new Date(),
            status: 'draft' as 'draft',  // ✨ Créer en draft pour vérification quota
            ...(event?.id && { id: event.id })
          };

          console.log('📝 [Publish] Création événement en draft pour vérification quota');

          // Créer ou mettre à jour l'événement en draft
          let eventId: string;
          if (event?.id) {
            // Mise à jour d'un événement existant
            await onSubmit(draftData);
            eventId = event.id;
          } else if (createdDraftEventIdRef.current) {
            // 🔁 FIX (robustesse réseau faible - item 5d): un brouillon a déjà été créé
            // lors d'une tentative de publication précédente (ex: publishEvent a échoué ou
            // timeout sur réseau lent APRÈS la création du brouillon). On réutilise son id
            // au lieu d'en recréer un nouveau, pour éviter les brouillons fantômes en
            // double quand l'utilisateur reclique sur "Publier".
            eventId = createdDraftEventIdRef.current;
            console.log(`🔁 [Publish] Réutilisation du brouillon déjà créé lors d'une tentative précédente (ID: ${eventId})`);
          } else {
            // Création d'un nouvel événement - besoin de récupérer l'ID
            eventId = await supabaseEventService.createEvent({
              ...draftData,
              organizerId
            });
            createdDraftEventIdRef.current = eventId;
          }

          console.log(`✅ [Publish] Événement créé en draft (ID: ${eventId}), appel RPC publish_event`);

          // ✨ Publier via RPC avec vérification quota côté serveur
          const publishResult = await stripeService.publishEvent(eventId);

          // Si limite atteinte, afficher PublishLimitModal
          if (!publishResult.success) {
            if (publishResult.error === 'limit_reached') {
              console.log('🚫 [Publish] Limite atteinte, affichage PublishLimitModal');
              setPublishLimitEventId(eventId);
              setPublishLimitQuota({
                used: publishResult.eventsUsed || 0,
                limit: publishResult.eventsLimit || 5
              });
              setShowPublishLimit(true);
              setIsSubmitting(false);
              return; // Ne pas fermer le formulaire, l'utilisateur doit choisir
            }

            // Autre erreur
            throw new Error(publishResult.error || 'Failed to publish event');
          }

          console.log('✅ [Publish] Événement publié avec succès via RPC');

          // Publication réussie: on peut oublier le brouillon mémorisé (plus besoin de le
          // réutiliser sur une éventuelle prochaine tentative).
          createdDraftEventIdRef.current = null;

          // ===== TRACKING PUBLICATION UNIQUE =====
          const tempsTotalCreation = Math.round((Date.now() - formStartTime) / 1000);
          const nombreSessionsCreation = 1;
          const etaitDraft = event?.status === 'draft';
          const completudeFinale = calculateCompleteness(formData, 'preview');

          analytics.trackEventPublished(
            eventId,
            tempsTotalCreation,
            nombreSessionsCreation,
            etaitDraft,
            completudeFinale,
            'unique'
          );

          console.log('🎉 [Analytics] Événement unique publié:', {
            id_evenement: eventId,
            temps_total_creation: tempsTotalCreation,
            etait_draft: etaitDraft,
            completude_finale: completudeFinale
          });

          showSuccessToast(false);
        } catch (error) {
          console.error('Erreur lors du nettoyage ou de la soumission:', error);
          throw error;
        }
      }

      onClose();
    } catch (error) {
      console.error("❌ Erreur critique lors de la publication:", error);
      console.error("Détails de l'erreur:", error instanceof Error ? error.message : String(error));
      toast.error('An error occurred during publication. Please try again.');
      setIsSubmitting(false);
    }
  };

  const { isLoaded, requestLoad } = useGoogleMaps();

  // Trigger le chargement de Google Maps quand le formulaire monte
  useEffect(() => {
    requestLoad();
  }, [requestLoad]);

  const handleTagToggle = (tagId: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.includes(tagId)
        ? prev.tags.filter(id => id !== tagId)
        : [...prev.tags, tagId]
    }));
  };

  // Fonction modifiée pour afficher un message aléatoire
  const showSuccessToast = (isDraft: boolean) => {
    const messageData = isDraft 
      ? getRandomToastMessage(draftSuccessMessages)
      : getRandomToastMessage(publishSuccessMessages);
      
    toast.success(
      <div>
        <strong>{messageData.title}</strong>
        <p>{messageData.message}</p>
      </div>,
      { duration: 5000 }
    );
  };

  const handlePlaceSelect = async (result: PlaceAutocompleteResult) => {
    const location = result.formattedAddress || '';
    const locationName = result.displayName || location.split(',')[0] || '';
    const lat = result.location?.lat;
    const lng = result.location?.lng;

    // Calculer le geohash pour indexation spatiale
    let locationGeohash: string | undefined;
    if (lat && lng) {
      locationGeohash = geohashLib.encode(lat, lng, GEOHASH_PRECISION);
      console.log(`📍 [Geohash] Localisation encodee: ${locationGeohash} (${lat}, ${lng})`);
    }

    // 🌍 Détection automatique du timezone depuis les coordonnées (tz-lookup est synchrone)
    let detectedTimezone = formData.timezone; // Conserver le timezone existant par défaut
    if (lat && lng) {
      console.log(`🌍 [Timezone] Détection du timezone pour (${lat}, ${lng})...`);
      detectedTimezone = getTimezoneFromCoords(lat, lng);
      console.log(`✅ [Timezone] Timezone détecté: ${detectedTimezone}`);
    }

    // Mise à jour des données du formulaire
    setFormData(prev => ({
      ...prev,
      location,
      locationName,
      latitude: lat,
      longitude: lng,
      geohash: locationGeohash,
      timezone: detectedTimezone // 🌍 Timezone auto-détecté
    }));

    // Afficher le champ pour le nom du lieu
    setShowLocationNameInput(true);
  };

  // Wrapper pour setFormData qui inclut le tracking
  const updateFormData = (updates: Partial<FormData> | ((prev: FormData) => FormData)) => {
    setFormData(prev => {
      const newData = typeof updates === 'function' ? updates(prev) : { ...prev, ...updates };
      
      // 🔍 Comparer prev et newData pour détecter les changements
      const prevAny = prev as Record<string, any>;
      const newDataAny = newData as Record<string, any>;
      
      const changedFields = Object.keys(newData).filter(key => 
        prevAny[key] !== newDataAny[key]
      );
      
      // 📝 Tracker les champs modifiés (avec debounce)
      if (changedFields.length > 0) {
        changedFields.forEach(key => {
          // Mettre à jour le compteur de modifications
          setFieldModificationCounts(prevCounts => ({
            ...prevCounts,
            [key]: (prevCounts[key] || 0) + 1
          }));
          
          // Log optimisé (seulement pour les changements significatifs)
          const newValue = newDataAny[key];
          if (typeof newValue === 'string' && newValue.length % 3 === 0 && newValue.length > 0) {
            console.log(`📝 [Analytics] Champ "${key}" modifié: "${newValue}" (${(fieldModificationCounts[key] || 0) + 1} modifications)`);
          }
        });
      }
      
      return newData;
    });
  };

  // Modifier la fonction onClose pour tracker l'abandon
  const handleClose = () => {
    if (true) { // Seulement si c'est une création (pas une édition)
      const tempsTotalSession = Math.round((Date.now() - formStartTime) / 1000);
      const champsRemplis = getFilledFields(formData);
      const completudeFinale = calculateCompleteness(formData, 
        currentStep === 0 ? 'basic_info' : currentStep === 1 ? 'details' : 'preview'
      );
      const etapeAbandon = currentStep === 0 ? 'basic_info' : currentStep === 1 ? 'details' : 'preview';
      
      // Déterminer la raison probable d'abandon
      const raisonProbable = Object.keys(errors).length > 0 ? 'erreur_validation' : 'fermeture_navigateur';
      
      analytics.trackCreationAbandoned(
        etapeAbandon,
        'unknown', // Dernier champ modifié - on pourrait l'améliorer
        tempsTotalSession,
        completudeFinale,
        raisonProbable,
        champsRemplis
      );
      
      console.log('🚪 [Analytics] Abandon de création tracké:', {
        etape_abandon: etapeAbandon,
        temps_total: tempsTotalSession,
        completude_finale: completudeFinale,
        raison: raisonProbable
      });
    }
    
    onClose();
  };

  return (
    <div className="event-form" ref={formRef}>
      <div className="event-form__header">
        <h2 className="event-form__title">
          {event ? 'Edit Event' : 'Create Event'}
          {event && (
            <span className={`event-status event-status--${event.status}`}>
              {event.status === 'draft' ? '· Draft' : '· Published'}
            </span>
          )}
        </h2>
        <button 
          className="event-form__close"
          onClick={handleClose}
          aria-label="Fermer le formulaire"
        >
          <Icon name="X" size={24} />
        </button>
      </div>

      {!hideStepperAndPreview && (
        <StepIndicator
          steps={FORM_STEPS}
          currentStep={currentStep}
          compact={mode === 'sidebar'}
          onStepClick={(stepIndex) => setCurrentStep(stepIndex)}
        />
      )}

      <div className="event-form__step">
      
          {currentStep === 0 && (
            <BasicInfoStep
              formData={formData}
              setFormData={updateFormData}
              errors={errors}
              isLoaded={isLoaded}
              showLocationNameInput={showLocationNameInput}
              setShowLocationNameInput={setShowLocationNameInput}
              isDraft={formData.status === 'draft'}
              onPlaceSelect={handlePlaceSelect}
              isLoadingTimezone={isLoadingTimezone}
            />
          )}

          {currentStep === 1 && (
            <DetailsStep
              formData={formData}
              setFormData={updateFormData}
              errors={errors}
              handleTagToggle={handleTagToggle}
              onImageUploading={setIsImageUploading}
            />
          )}

          {currentStep === 2 && !hideStepperAndPreview && (
            <PreviewStep formData={formData} />
          )}
        
      </div>

      <div className="event-form__buttons-container">
        {currentStep > 0 && (
          <Button 
            variant="outline"
            size="md"
            onClick={handlePrevious}
            className="event-form__button event-form__button--back"
          >
            <Icon name="ArrowLeft" size={16} />
            Back
          </Button>
        )}
        
        <div className="event-form__spacer"></div>
        
        <div className="event-form__right-buttons">
          <Button 
            variant="outline"
            size="md"
            onClick={handleSaveAsDraft}
            disabled={isSubmitting || isImageUploading || !formData.title}
            className="event-form__button event-form__button--draft"
          >
            {isSubmitting ? (
              <>
                <span className="loading-spinner"></span>
                Saving...
              </>
            ) : (
              <>
                <Icon name="Save" size={16} />
                Save as Draft
              </>
            )}
          </Button>
          
          {/* Show Next button if not on last step (step 1 for hideStepperAndPreview, step 2 otherwise) */}
          {currentStep < (hideStepperAndPreview ? 1 : FORM_STEPS.length - 1) ? (
            <Button
              variant="primary"
              size="md"
              onClick={handleNext}
              className="event-form__button event-form__button--next"
            >
              Next
              <Icon name="ArrowRight" size={16} />
            </Button>
          ) : (
            <Button
              variant="primary"
              size="md"
              onClick={handlePublish}
              disabled={isSubmitting || isImageUploading}
              className="event-form__button event-form__button--publish"
            >
              {isSubmitting ? (
                <>
                  <span className="loading-spinner"></span>
                  Publishing...
                </>
              ) : (
                <>
                  <Icon name="Send" size={16} />
                  Publish
                </>
              )}
            </Button>
          )}
        </div>
      </div>

      {/* Confirmation Modal for Recurrence Changes */}
      <ConfirmationModal
        isOpen={recurrenceConfirm.isOpen}
        onClose={() => setRecurrenceConfirm({ isOpen: false, preview: null, pendingAction: null, variant: 'warning' })}
        onConfirm={async () => {
          if (recurrenceConfirm.pendingAction) {
            setIsSubmitting(true);
            try {
              await recurrenceConfirm.pendingAction();
            } catch (error) {
              console.error('Error executing recurrence change:', error);
              toast.error('Failed to update series. Please try again.');
            } finally {
              setIsSubmitting(false);
              setRecurrenceConfirm({ isOpen: false, preview: null, pendingAction: null, variant: 'warning' });
            }
          }
        }}
        title={recurrenceConfirm.preview?.toDelete?.length ? 'Confirm Series Changes' : 'Update Series'}
        message={
          recurrenceConfirm.preview?.toDelete?.length
            ? `This change will remove ${recurrenceConfirm.preview.toDelete.length} occurrence(s) from outside the new date range.`
            : `This will update your recurring series with ${recurrenceConfirm.preview?.toCreate?.length || 0} new occurrence(s).`
        }
        items={recurrenceConfirm.preview?.toDelete?.slice(0, 5).map(item => ({
          label: new Date(item.datetime).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
          sublabel: new Date(item.datetime).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
        }))}
        confirmText={recurrenceConfirm.preview?.toDelete?.length ? 'Yes, Proceed' : 'Update Series'}
        cancelText="Cancel"
        variant={recurrenceConfirm.variant}
        isLoading={isSubmitting}
      />

      {/* Publish Limit Modal (pay-per-event) */}
      {publishLimitEventId && organizerProfile && (
        <PublishLimitModal
          isOpen={showPublishLimit}
          onClose={() => {
            setShowPublishLimit(false);
            setPublishLimitEventId(null);
            // Don't close the form - user might want to save as draft instead
          }}
          eventId={publishLimitEventId}
          organizerId={organizerProfile.id}
          eventsUsed={publishLimitQuota.used}
          eventsLimit={publishLimitQuota.limit}
        />
      )}
    </div>
  );
};

export default EventForm;
